import { DPA_PATH } from '../route.ts';
import type { LegalDocument } from './documents.ts';

/**
 * The data processing agreement, as data.
 *
 * Drafted by the same author as `privacyContent.ts`, deliberately: the two must
 * agree with each other. A DPA is worthless if the privacy policy describes
 * different data, a different sub-processor list or a different retention
 * position, and the fastest way to produce that contradiction is to write them
 * separately. They cross-reference each other by section id.
 *
 * Two things a template would get wrong and this one must not. The annex of
 * security measures describes what this system actually does, in terms a
 * customer’s auditor can check against the source, rather than a generic list.
 * And it states plainly that no automated decision-making about individuals
 * occurs, because a reader who sees a language model in the architecture will
 * otherwise assume it decides something.
 */
export const DPA: LegalDocument = {
  id: 'dpa',
  path: DPA_PATH,
  draft: true,
  title: 'Data processing agreement',
  version: '0.2.1-draft',
  updated: '2026-09-25',
  summary:
    'The Article 28 terms on which DriftWatch processes personal data for a customer — as ' +
    'processor where a customer assesses its own tenant, and as sub-processor where the customer ' +
    'is a managed service provider assessing a client’s. Draft for legal review.',
  sections: [
    {
      id: 'draft-status',
      heading: 'Status of this document',
      paragraphs: [
        'This is a draft for legal review. It has not been reviewed or approved by a qualified ' +
          'data protection lawyer, it has not been executed by anybody, and it does not yet bind ' +
          'Drift Watch or any customer.',
        'It is published in draft because the substance of it — what is processed, what is ' +
          'refused, where it runs, who else is involved and what the security measures actually ' +
          'are — is drawn from the running system and is useful to a reviewer now. The commercial ' +
          'and jurisdictional terms are not settled and appear as square-bracketed placeholders, ' +
          'listed in full at the end.',
        'This agreement and the DriftWatch privacy policy are drafted together and must be read ' +
          'together. Where they describe the same processing, they are intended to say the same ' +
          'thing; if they do not, that is a defect and the resolution is in the precedence ' +
          'section below.',
      ],
    },
    {
      id: 'parties',
      heading: 'Parties',
      paragraphs: [
        'This agreement is between Drift Watch, company number [COMPANY NUMBER], of [REGISTERED ' +
          'ADDRESS] ("DriftWatch"), and [CUSTOMER NAME], of [CUSTOMER REGISTERED ADDRESS] (the ' +
          '"Customer").',
        'It takes effect on [EFFECTIVE DATE] and forms part of the agreement under which ' +
          'DriftWatch provides the service to the Customer.',
        'Contact for matters under this agreement: [PRIVACY CONTACT EMAIL]. Where a data ' +
          'protection officer or Article 27 representative is appointed for DriftWatch, it is ' +
          '[DPO OR REPRESENTATIVE].',
      ],
    },
    {
      id: 'definitions',
      heading: 'Definitions',
      paragraphs: [
        '"Data Protection Law" means the UK GDPR, the Data Protection Act 2018, Regulation (EU) ' +
          '2016/679 and any other data protection law applicable to the processing under this ' +
          'agreement.',
        '"Controller", "processor", "personal data", "processing", "data subject" and "personal ' +
          'data breach" have the meanings given in Data Protection Law.',
        '"Tenant" means a Microsoft 365 and Microsoft Entra tenant that the Customer has ' +
          'connected to the service for assessment. "Tenant Personal Data" means personal data ' +
          'contained in the configuration of a Tenant and processed by DriftWatch under this ' +
          'agreement.',
        '"Operator Account Data" means personal data about the individuals the Customer ' +
          'authorises to use the DriftWatch console: their email address, display name, stored ' +
          'password hash, session records and sign-in attempt records.',
        '"Support Correspondence" means a support ticket raised through the console and ' +
          'everything in it: its subject and type, each message written by the Customer’s ' +
          'operators or by DriftWatch, any image attached to a message, the display name ' +
          'recorded against each message, and when each was sent.',
      ],
    },
    {
      id: 'roles',
      heading: 'Controller and processor, in each of the two cases',
      paragraphs: [
        'DriftWatch supports two arrangements. They are genuinely different and this agreement ' +
          'does not pretend otherwise. Which one applies depends on whose tenant is being ' +
          'assessed, and an account may hold tenants in both categories at once.',
        'Case one — the Customer assesses its own Tenant. The Customer is the controller of the ' +
          'Tenant Personal Data. DriftWatch is its processor. The Customer determines the purpose ' +
          'and the lawful basis, and instructs DriftWatch; DriftWatch processes on those ' +
          'instructions and on no others.',
        'Case two — the Customer is a managed service provider assessing a client’s Tenant. The ' +
          'Customer’s client is the controller. The Customer is that client’s processor. ' +
          'DriftWatch is a sub-processor, engaged by the Customer under the Customer’s own ' +
          'agreement with its client. DriftWatch takes instructions from the Customer, and the ' +
          'Customer warrants that those instructions are consistent with what its client has ' +
          'authorised.',
        'In case two the Customer must have its client’s authorisation to engage DriftWatch as a ' +
          'sub-processor, and must have flowed down obligations to its client that are no less ' +
          'protective than those in this agreement. DriftWatch has no direct relationship with ' +
          'the Customer’s client and does not purport to act as its processor directly. Nothing ' +
          'in this agreement makes the Customer’s client a party to it.',
        'In both cases DriftWatch is the controller of a small amount of data about visitors to ' +
          'its public website — the name, email address and optional details given to join the ' +
          'waiting list, and the IP address each sign-up came from. That is outside ' +
          'this agreement and is described in the privacy policy, section what-we-collect.',
        'Operator Account Data is processed by DriftWatch as processor for the Customer, on the ' +
          'same terms as Tenant Personal Data, in both cases.',
        'Support Correspondence is DriftWatch’s, as controller: DriftWatch decides what a ' +
          'support conversation needs and what happens to it, and it is described in the ' +
          'privacy policy, section support-tickets, rather than governed by this agreement. The ' +
          'display name recorded against a message is copied from the author’s Operator Account ' +
          'Data when the message is sent and from then on forms part of the correspondence.',
        'One exception keeps that line honest. Where the Customer’s operators put Tenant ' +
          'Personal Data into a ticket — a screenshot of a report that names users, say, or a ' +
          'pasted list of accounts — that data remains the controller’s, as it was before it was ' +
          'sent. DriftWatch processes it under this agreement, as processor in case one and as ' +
          'sub-processor in case two, on the instruction the Customer gives by including it, and ' +
          'only to answer that ticket.',
      ],
    },
    {
      id: 'subject-matter',
      heading: 'Subject matter, duration, nature and purpose',
      paragraphs: [
        'Subject matter: the read-only assessment of a Tenant’s security configuration against a ' +
          'versioned catalogue of controls, and the production of a report on it.',
        'Duration: for the term of the agreement under which DriftWatch provides the service, and ' +
          'thereafter only as set out in the deletion section below.',
        'Nature of the processing: collection of Tenant configuration from Microsoft Graph on a ' +
          'read-only basis; normalisation into an internal schema; deterministic evaluation ' +
          'against control predicates; storage of the resulting snapshots and findings; ' +
          'generation and rendering of reports; and, in an environment where the narrative layer ' +
          'is switched on, generation of explanatory prose by a third-party model from redacted ' +
          'findings (see anthropic-optional). Also, where the Customer includes it, storage and ' +
          'reading of Tenant Personal Data inside a support ticket, to answer that ticket.',
        'Purpose: to tell the controller how its Microsoft 365 tenant is configured relative to ' +
          'recognised security and compliance frameworks, and what to change. DriftWatch ' +
          'processes for no other purpose, and does not use Tenant Personal Data to train any ' +
          'model, to build any profile, or for any product of its own beyond providing the ' +
          'service to that Customer.',
        'Full details of the processing are in the annex, section annex-processing.',
      ],
    },
    {
      id: 'instructions',
      heading: 'Processing on documented instructions',
      paragraphs: [
        'DriftWatch processes personal data only on the Customer’s documented instructions, ' +
          'including as to transfers to a third country, unless required to do otherwise by law. ' +
          'Where the law requires it, DriftWatch will inform the Customer before processing ' +
          'unless that law prohibits it on important grounds of public interest.',
        'This agreement, the service documentation and the Customer’s use of the service through ' +
          'the console and the API together constitute the Customer’s documented instructions. ' +
          'Connecting a Tenant is an instruction to collect from it; running or scheduling a scan ' +
          'is an instruction to assess it. The service does not yet offer a per-customer choice ' +
          'about the narrative layer — it is switched on or off for a whole environment — so the ' +
          'product itself does not record a Customer’s instruction to transfer redacted findings ' +
          'to Anthropic PBC. See anthropic-optional.',
        'DriftWatch will inform the Customer immediately if, in its opinion, an instruction ' +
          'infringes Data Protection Law, and may suspend the processing concerned until the ' +
          'instruction is withdrawn or amended.',
        'The service is technically incapable of writing to a Tenant, so no instruction to modify ' +
          'a Customer’s Microsoft 365 environment can be accepted or carried out. See the section ' +
          'on read-only access in annex-security-measures.',
      ],
    },
    {
      id: 'controller-obligations',
      heading: 'The Customer’s obligations',
      paragraphs: [
        'The Customer warrants that it has a lawful basis for the processing it instructs, that ' +
          'it has given any notice and obtained any authorisation required, and that its ' +
          'instructions comply with Data Protection Law.',
        'In case two above, the Customer additionally warrants that its client has authorised the ' +
          'engagement of sub-processors, that DriftWatch may be engaged as one, and that the ' +
          'Customer will pass on to its client anything DriftWatch provides under this agreement ' +
          'which that client is entitled to receive — including notice of a personal data breach ' +
          'and notice of a change of sub-processor.',
        'The Customer is responsible for the accounts it creates in the console, for the ' +
          'capabilities it grants them, and for removing an account when the person no longer ' +
          'needs it. DriftWatch enforces the capabilities the Customer sets; it does not decide ' +
          'who should have them.',
        'The Customer decides which Tenants it connects. DriftWatch does not discover, suggest or ' +
          'connect Tenants on its own initiative.',
        'The Customer will ensure its operators include in a support ticket only the Tenant ' +
          'Personal Data a question needs, cropping or obscuring the rest of a screenshot before ' +
          'it is attached. Every operator in the Customer’s account can read all of that ' +
          'account’s tickets, so the Customer’s choice of who has an account is also its choice ' +
          'of who can read them.',
      ],
    },
    {
      id: 'confidentiality',
      heading: 'Confidentiality',
      paragraphs: [
        'DriftWatch keeps personal data processed under this agreement confidential and discloses ' +
          'it to no third party except as this agreement permits, or as required by law.',
        'Every person authorised by DriftWatch to process the personal data is bound by an ' +
          'obligation of confidentiality, whether contractual or statutory, that survives the end ' +
          'of their engagement.',
        'Access is limited to those who need it to provide or support the service. DriftWatch is ' +
          'a single-maintainer operation at the date of this draft, and the honest statement of ' +
          'the position is that the number of people with administrative access to the production ' +
          'environment is one. Access is not a matter of policy alone: the console enforces ' +
          'capability-based authorisation denying by default, and operator actions are recorded in ' +
          'an audit log.',
      ],
    },
    {
      id: 'security-measures',
      heading: 'Security of processing',
      paragraphs: [
        'DriftWatch implements appropriate technical and organisational measures to ensure a ' +
          'level of security appropriate to the risk, taking account of the state of the art, the ' +
          'costs of implementation, and the nature, scope, context and purposes of the ' +
          'processing, as required by Article 32.',
        'Those measures are set out specifically, and not generically, in the annex at section ' +
          'annex-security-measures. They are described there in terms a reviewer can check ' +
          'against the behaviour of the system rather than take on trust, and the annex also ' +
          'states what DriftWatch does not have.',
        'DriftWatch will not materially reduce the measures described in that annex during the ' +
          'term of this agreement. It may change how a measure is implemented, provided the ' +
          'protection is not reduced.',
      ],
    },
    {
      id: 'subprocessors',
      heading: 'Sub-processors',
      paragraphs: [
        'The Customer gives general authorisation for DriftWatch to engage the sub-processors ' +
          'listed at annex-subprocessors.',
        'DriftWatch will give the Customer at least [SUB-PROCESSOR OBJECTION PERIOD] notice ' +
          'before adding or replacing a sub-processor. The Customer may object on reasonable data ' +
          'protection grounds within that period; if the objection cannot be resolved, the ' +
          'Customer may terminate the affected part of the service.',
        'Where DriftWatch engages a sub-processor, it imposes on it by contract data protection ' +
          'obligations no less protective than those in this agreement, and remains fully liable ' +
          'to the Customer for that sub-processor’s performance.',
        'Support tickets reach no sub-processor other than IONOS SE, which hosts them. They are ' +
          'not sent to Anthropic PBC or to any email or ticketing service; replies appear in the ' +
          'console.',
        'Microsoft is not a sub-processor of DriftWatch. The Tenant being assessed is the ' +
          'Customer’s own service under the Customer’s own agreement with Microsoft. It is the ' +
          'source of the data, not a destination for it.',
      ],
    },
    {
      id: 'anthropic-optional',
      heading: 'The narrative layer, where it runs, and what reaches Anthropic',
      paragraphs: [
        'DriftWatch can use Anthropic PBC, in the United States, to generate the explanatory ' +
          'prose in a report. The service runs fully without it, falling back to static ' +
          'remediation text written in advance. Running with it disabled is a supported ' +
          'configuration, not a degraded one.',
        'It is switched on or off for a whole DriftWatch environment, not per Customer. It runs ' +
          'wherever an environment holds an Anthropic API key and has not been told to switch it ' +
          'off. There is no per-Customer choice in the service yet, and so no way for a Customer ' +
          'to give or withhold, through the product, the instruction this agreement requires for ' +
          'the transfer.',
        'The position at 22 September 2026. In staging it is on, and staging assesses one real ' +
          'customer Tenant, connected through that customer’s own consent. In production it has ' +
          'been off since 21 September 2026. It was meant to be off before then and was not: a ' +
          'switch written as "true" was read by code that recognised only "1", and on 19 ' +
          'September 2026 production sent Anthropic redacted findings about the one Tenant it had ' +
          'connected, in forty-five requests. Stricter reading of every switch, refusing to start ' +
          'on a value it does not recognise, reaches production with its next release. ' +
          'Production stays off until the transfer safeguards at transfers are in place.',
        'The model is asked only about findings the rules engine has already decided — failures, ' +
          'and some findings it could not assess — and never about a pass, or about a finding ' +
          'whose text is already cached. What is sent is the control’s own catalogue text, the ' +
          'verdict, the thresholds applied and the redacted evidence.',
        'Everything that leaves DriftWatch for Anthropic passes through a single point in ' +
          'the code which is the only place permitted to construct an outbound prompt. Evidence ' +
          'is classified at the moment it is created — person, group, service principal, policy, ' +
          'domain, tenant, administrator-authored free text, or configuration. Everything but ' +
          'configuration and the names of Microsoft’s own built-in roles is replaced by a stable ' +
          'pseudonym before it can be sent; a custom role, being named by an administrator, is ' +
          'pseudonymised.',
        'The redacted payload is then scanned. If an email address, an external guest identifier, ' +
          'an object identifier that is not one of Microsoft’s published constants, or any ' +
          'original value that should have been pseudonymised survives, the scan raises an error ' +
          'and the operation fails. The design fails closed: a mis-classification the scanner ' +
          'recognises breaks the scan rather than becoming a disclosure. The refusal records the ' +
          'kind of value, the control and the evidence position, never the value. Until 22 ' +
          'September 2026 the blocked value was also written to the scan record, the job queue ' +
          'and the worker log; it no longer is.',
        'The scanner checks the classification; it does not replace it. A person’s name ' +
          'mis-classified as configuration, and appearing nowhere else in the same finding, would ' +
          'not be recognised by it. Classification at the point of construction, and a test that ' +
          'runs every control over synthetic test tenants through the chokepoint, are the ' +
          'controls against that.',
        'Generated text is stored pseudonymised. Real values are substituted only when a report ' +
          'is rendered, from a mapping that never leaves DriftWatch infrastructure. Because what ' +
          'is stored contains tokens and no identifiers, generated text may be cached and reused ' +
          'across customers; no Customer’s personal data is exposed to another by that cache.',
        'Anthropic’s published Commercial Terms of Service (effective 17 June 2025), which govern ' +
          'use of its API, state that it may not train models on customer content, and ' +
          'incorporate its Data Processing Addendum (effective 24 February 2025), under which it ' +
          'acts as processor and deletes customer data within thirty days of termination. Its ' +
          'published retention policy is to delete API inputs and outputs within 30 days, longer ' +
          'only to enforce its usage policy or where the law requires (Anthropic Privacy Center, ' +
          'updated 1 July 2026). DriftWatch relies on those terms as published and has no ' +
          'separately negotiated agreement with Anthropic.',
        'The same mechanism is described for data subjects in the privacy policy, section ' +
          'ai-processing.',
      ],
    },
    {
      id: 'automated-decisions',
      heading: 'No automated decision-making about individuals',
      paragraphs: [
        'No automated decision-making, including profiling, producing legal effects concerning ' +
          'an individual or similarly significantly affecting them, takes place in the service.',
        'This is stated plainly because a reader who sees a large language model in the ' +
          'architecture will assume the opposite. Every verdict is produced by a deterministic ' +
          'rules engine: a pure, unit-tested predicate over a normalised model of the Tenant ' +
          'configuration, returning a pass, a fail, a not-applicable or an insufficient-data ' +
          'result together with the evidence that produced it and the version of the rule that ' +
          'produced it. The same configuration produces the same verdict every time.',
        'The model, where it is used at all, writes prose about a decision already made. It is ' +
          'never asked whether something is compliant and cannot change a verdict. Where the ' +
          'prose and the verdict disagree, the verdict governs and the prose is a defect.',
        'Findings describe the configuration of a Tenant, not the conduct of a person. Where a ' +
          'finding names an account, it is a statement about how that account is configured. Any ' +
          'decision about an individual taken in response is the controller’s decision, taken by ' +
          'people exercising their own judgement on the report.',
      ],
    },
    {
      id: 'data-subject-requests',
      heading: 'Assisting with data subject requests',
      paragraphs: [
        'Taking into account the nature of the processing, DriftWatch assists the Customer by ' +
          'appropriate technical and organisational measures, so far as possible, in fulfilling ' +
          'the Customer’s obligation to respond to requests to exercise data subject rights.',
        'DriftWatch will not respond to such a request itself unless the Customer instructs it ' +
          'to, or the law requires it. If a data subject contacts DriftWatch directly, DriftWatch ' +
          'will tell them it is a processor, will identify the controller to them where it can, ' +
          'and will notify the Customer without undue delay. In case two above, DriftWatch ' +
          'notifies the Customer, and the Customer is responsible for notifying its client.',
        'The practical position is worth stating: much of what DriftWatch holds about an ' +
          'individual is a pseudonymous directory identifier, a user principal name and a display ' +
          'name, all of which originate in the Customer’s own Tenant and are authoritative there. ' +
          'Rectification is generally achieved at source and reflected at the next scan, rather ' +
          'than by editing a stored finding, which is a record of what the configuration was at ' +
          'a point in time.',
      ],
    },
    {
      id: 'assistance',
      heading: 'Other assistance to the Customer',
      paragraphs: [
        'DriftWatch assists the Customer, taking into account the nature of the processing and ' +
          'the information available to it, in ensuring compliance with the obligations in ' +
          'Articles 32 to 36: security of processing, breach notification to the supervisory ' +
          'authority and to data subjects, data protection impact assessments, and prior ' +
          'consultation.',
        'In practice that means providing the information a Customer needs to complete an impact ' +
          'assessment covering this processing, responding to security questionnaires and ' +
          'due-diligence requests, and giving the details of an incident that a Customer needs in ' +
          'order to make its own notification decision.',
        'DriftWatch makes available to the Customer all information necessary to demonstrate ' +
          'compliance with Article 28.',
      ],
    },
    {
      id: 'breach-notification',
      heading: 'Personal data breaches',
      paragraphs: [
        'DriftWatch notifies the Customer without undue delay after becoming aware of a personal ' +
          'data breach affecting personal data processed under this agreement, and in any event ' +
          'within [BREACH NOTIFICATION DEADLINE].',
        'The notification describes, so far as it is known at the time: the nature of the breach ' +
          'including the categories and approximate number of data subjects and records ' +
          'concerned; the likely consequences; the measures taken or proposed to address it and ' +
          'to mitigate its effects; and a contact point for further information. Where the full ' +
          'picture is not available at once, DriftWatch provides it in phases without further ' +
          'undue delay rather than waiting to send one complete notification.',
        'DriftWatch does not notify a supervisory authority or a data subject on the Customer’s ' +
          'behalf unless instructed to do so. That decision belongs to the controller.',
        'In case two above, DriftWatch notifies the Customer, and the Customer is responsible for ' +
          'notifying its client so that the client can meet its own controller obligations.',
        'DriftWatch also notifies the Customer of any request for disclosure of personal data ' +
          'received from a public authority, unless prohibited from doing so by law.',
      ],
    },
    {
      id: 'audits',
      heading: 'Audits and information',
      paragraphs: [
        'DriftWatch makes available to the Customer the information necessary to demonstrate ' +
          'compliance with the obligations in Article 28, and allows for and contributes to ' +
          'audits, including inspections, conducted by the Customer or another auditor it ' +
          'mandates.',
        'The honest position on what is available today: there is no SOC 2 report, no ISO/IEC ' +
          '27001 certification and no penetration test report for DriftWatch, so an audit cannot ' +
          'be satisfied by handing over one of those. This is set out in full at ' +
          'annex-not-claimed. DriftWatch assesses other organisations against those frameworks; ' +
          'it does not hold them, and it will not imply that it does.',
        'What is available instead is more specific than most third-party reports: the security ' +
          'measures in annex-security-measures are mechanisms in the source code with tests ' +
          'asserting them, and DriftWatch will walk an auditor through any of them, demonstrate ' +
          'the behaviour, and provide the corresponding evidence.',
        'Audits are conducted on reasonable notice, during business hours, no more than once in ' +
          'any twelve-month period unless a personal data breach or a regulator requires ' +
          'otherwise, and subject to confidentiality. An audit must not compromise the security ' +
          'or the isolation of any other customer’s data, and DriftWatch will decline any step ' +
          'that would.',
      ],
    },
    {
      id: 'transfers',
      heading: 'International transfers',
      paragraphs: [
        'The service runs on a single host in Frankfurt am Main, Germany, operated by IONOS SE. ' +
          'The PostgreSQL database and the Redis job queue are on that host. Hosting is therefore ' +
          'within the EEA and needs no transfer mechanism of its own.',
        'The only transfer outside the EEA contemplated by this agreement is to Anthropic PBC in ' +
          'the United States, through the narrative layer. It is made in staging, which assesses ' +
          'one real customer Tenant; it was made in production on 19 September 2026; and ' +
          'production has had it switched off since 21 September 2026. The data transferred has ' +
          'been pseudonymised and leak-scanned as described at anthropic-optional.',
        'What is not yet in place. Anthropic’s Data Processing Addendum, incorporated into its ' +
          'Commercial Terms, contains the EU Standard Contractual Clauses (module two, controller ' +
          'to processor, and module three, processor to processor) and the UK International Data ' +
          'Transfer Addendum. DriftWatch has not yet recorded that as the mechanism it relies on, ' +
          'which is shown as [TRANSFER MECHANISM], and has not yet carried out or documented a ' +
          'transfer risk assessment. This agreement previously said that assessment would be done ' +
          'before the transfer was relied on in production. It was not: the transfer was made in ' +
          'production on 19 September 2026 through the fault described at anthropic-optional, and ' +
          'staging has been making it without one. Production stays switched off until both are ' +
          'in place.',
        'DriftWatch does not transfer personal data to any other third country, and will not do ' +
          'so without first adding the recipient as a sub-processor under the notice and ' +
          'objection process above.',
      ],
    },
    {
      id: 'deletion',
      heading: 'Deletion and return',
      paragraphs: [
        'At the Customer’s choice, DriftWatch deletes or returns all personal data processed ' +
          'under this agreement at the end of the provision of the service, and deletes existing ' +
          'copies, unless the law requires it to keep them.',
        'Return is available as an export of the Customer’s reports and findings. Deletion of a ' +
          'connected Tenant cascades and removes its collected evidence with it.',
        'Support tickets are held against the Customer’s account rather than against a Tenant, ' +
          'so purging a Tenant does not remove them. They are deleted with the account, and ' +
          'DriftWatch will delete a ticket, or an image in one, when the Customer asks — which is ' +
          'how Tenant Personal Data included in a ticket is removed during the term. A message ' +
          'keeps its author’s display name after that operator’s account is removed, so that the ' +
          'conversation still reads; the Customer can ask for it to be removed or anonymised.',
        'The position on retention during the term must be stated accurately, because it is a ' +
          'gap. There is today no automatic retention or deletion schedule for scans, snapshots ' +
          'or findings: nothing ages out on its own. Expired sessions are purged, and a Tenant ' +
          'purge removes that Tenant’s evidence on request. The schedule that will close this gap ' +
          'is [RETENTION SCHEDULE]. Until it exists, deletion happens because the Customer asks ' +
          'for it, and DriftWatch will act on such a request rather than waiting for a policy to ' +
          'expire the data.',
        'Backups are nightly database dumps kept for seven days on the same host. There is no ' +
          'off-site copy, and this should not be described as disaster recovery. A deletion ' +
          'request is satisfied in the live database immediately and works through the backup ' +
          'rotation within seven days.',
        'The same position appears, in plainer terms, in the privacy policy, section retention.',
      ],
    },
    {
      id: 'precedence',
      heading: 'Precedence, liability and governing law',
      paragraphs: [
        'Where this agreement conflicts with the main services agreement on a matter of data ' +
          'protection, this agreement prevails. Where it conflicts with the DriftWatch privacy ' +
          'policy, this agreement prevails as between DriftWatch and the Customer; the privacy ' +
          'policy is an explanation for data subjects and is not a contract with the Customer.',
        'A conflict between the two on a matter of fact is a defect rather than a drafting ' +
          'choice, and should be reported to [PRIVACY CONTACT EMAIL] so that both are corrected.',
        'Each party’s liability under this agreement is subject to the limitations and exclusions ' +
          'in the main services agreement, except to the extent Data Protection Law does not ' +
          'permit that.',
        'This agreement is governed by [GOVERNING LAW] and the courts of [GOVERNING LAW] have ' +
          'exclusive jurisdiction, without prejudice to a data subject’s rights under Data ' +
          'Protection Law.',
        'DriftWatch’s lead supervisory authority is [SUPERVISORY AUTHORITY].',
      ],
    },
    {
      id: 'annex-processing',
      heading: 'Annex 1 — Details of the processing',
      paragraphs: [
        'Categories of data subject: individuals with accounts in a Customer’s Microsoft 365 and ' +
          'Entra tenant, including employees, contractors, guest and external users, and service ' +
          'accounts to the extent they identify a person; and the Customer’s own staff who use ' +
          'the DriftWatch console.',
        'Types of personal data, read from the configured sources listed in the source ' +
          'catalogue and covering configuration rather than content:',
        'Special category data: none is sought. DriftWatch does not read message bodies, file ' +
          'contents, calendar entries or SharePoint documents, and holds no permission that would ' +
          'allow it to. Directory display names and group names are free text written by the ' +
          'Customer’s administrators and could in principle contain anything, which is why every ' +
          'such value is classified as free text and pseudonymised before it can leave.',
        'Data that is deliberately not collected, which is set out here because it materially ' +
          'reduces the scope of the processing and is verifiable in the source:',
        'Frequency: on demand and on the schedule the Customer configures. Duration: as set out ' +
          'in the deletion section above. The corresponding plain-language description for data ' +
          'subjects is in the privacy policy, sections tenant-data and what-we-do-not-collect.',
      ],
      bullets: [
        'User principal names, display names and directory identifiers',
        'Last sign-in dates, used to assess dormant accounts',
        'Group names, identifiers and membership',
        'Directory role assignments, including privileged ones',
        'The directory identifier of a mailbox owner where an inbox rule forwards mail',
        'The directory identifier of the account behind each shared or Bookings mailbox, used to assess whether it can be signed in to',
        'Names that administrators give to Conditional Access policies, mail flow rules, ' +
          'Safe Links, Safe Attachments and anti-phishing policies and rules, and data loss ' +
          'prevention and sensitivity label policies, which can incidentally name a person or a ' +
          'partner',
        'Whether each account has registered a multi-factor authentication method its organisation ' +
          'allows, as two yes-or-no answers against its directory identifier',
        'Operator Account Data: email address, display name, scrypt password hash, session ' +
          'records and sign-in attempt records',
        'Tenant Personal Data that the Customer’s operators choose to include in a support ' +
          'ticket or an image attached to one. Not sought, and bounded only by what the Customer ' +
          'sends; each image is limited to a PNG, JPEG or WebP file of at most 700,000 bytes',
        'Not collected: device names, serial numbers, IMEI and MEID numbers, telephone numbers, ' +
          'ICCIDs and MAC addresses',
        'Not collected: IP addresses and geographic locations from Identity Protection risk ' +
          'detections',
        'Not collected: email addresses of mail-forwarding recipients — the local part is ' +
          'discarded in the collector and only the destination domain is stored',
        'Not collected: sensitivity-label descriptions and tooltips, which are ' +
          'administrator-written prose and routinely name internal projects and clients',
        'Not collected: the rule expressions in Conditional Access device, application and ' +
          'workload-identity filters — the collector keeps only the filter’s mode and, for a ' +
          'device filter, which of Microsoft’s documented device properties it tests',
        'Not collected: the identifiers of external tenants enumerated in a Conditional Access ' +
          'guest condition — only their number is stored',
        'The two Conditional Access exclusions above apply to collections from 21 September ' +
          '2026. Snapshots stored before that date hold the full policies until they are ' +
          'rewritten or deleted',
        'Not collected: the contents of Intune configuration profiles, which would otherwise ' +
          'arrive as a complete configuration payload',
        'Not collected: per-site SharePoint sharing, sharing links, and the identities of guests',
        'Not collected: any name, address or identifier for a mailbox’s SMTP AUTH setting — only ' +
          'whether each mailbox follows, enables or disables it, with no way to tell which mailbox ' +
          'is which',
        'Not collected: the partner domains a calendar sharing policy names, or the domains and ' +
          'addresses exempted from Outlook’s external tag — only how many there are and, for ' +
          'calendar sharing, the most detail they are allowed',
        'Not collected: the names, sign-in addresses and registered authentication methods ' +
          'Microsoft’s MFA registration report offers — only whether each account is registered',
        'Not collected: the people an anti-phishing policy protects from impersonation — only ' +
          'their number is stored — or the protected domains, excluded senders and excluded ' +
          'domains it lists',
        'Not collected: the recipient addresses, group names and other organisations’ domains ' +
          'that decide whom a Safe Links, Safe Attachments, anti-phishing or preset security ' +
          'policy rule applies to — they are reduced to yes-or-no answers and counts, and only ' +
          'the organisation’s own accepted domains may be kept',
        'The two mail-protection exclusions above apply to collections from 21 September 2026. ' +
          'Snapshots stored before that date hold the anti-phishing policies in full until they ' +
          'are rewritten or deleted',
        'Not collected: the sites, mailboxes, users, groups and teams a data loss prevention or ' +
          'sensitivity label policy names, and which labels it publishes — each location list is ' +
          'reduced in the collector to all, a count, or none, with a count of exceptions and of ' +
          'labels',
        'Not collected: the conditions and actions of a data loss prevention rule — sensitive ' +
          'information types, keywords, patterns, thresholds, actions and notification ' +
          'recipients — or the author, last editor and comments of any Purview policy. A rule ' +
          'is stored as the name of its policy and whether it is switched on',
      ],
    },
    {
      id: 'annex-security-measures',
      heading: 'Annex 2 — Technical and organisational measures',
      paragraphs: [
        'Each measure below is implemented in the system rather than described in a policy, and ' +
          'most have a test asserting them which fails the build if the property stops holding. ' +
          'That is the distinction worth checking: a control that depends on somebody remembering ' +
          'it is not a control.',
        'Read-only access, enforced three ways. DriftWatch holds twenty-one Microsoft Graph ' +
          'application permissions in a frozen list, every one of them a read scope. The HTTP ' +
          'transport refuses any method but GET, so a write cannot be issued even if a permission ' +
          'existed. A test greps the source tree for write-scope strings and fails the build if ' +
          'one appears. Adding a write capability is an architectural decision with its own ' +
          'review, not a configuration change.',
        'Access outside Microsoft Graph. Exchange Online and SharePoint publish no read-only ' +
          'application permission, so DriftWatch holds Exchange.ManageAsApp and ' +
          'Sites.FullControl.All, both capable of more than reading. A frozen list of read-only ' +
          'commands, a runner that accepts no other command, and a re-reading of every generated ' +
          'script against that list confine them to reading. For Exchange, each assessment also ' +
          'verifies that the account holds only view-only Exchange roles and no Entra directory ' +
          'role other than Teams Reader, and reads nothing from Exchange if either cannot be ' +
          'shown. Microsoft Purview is reached ' +
          'through the Exchange permission and a Purview role group the Customer creates, ' +
          'holding three view-only roles; those roles also allow six commands that are not ' +
          'reads (Set-DlpEdmSchema, Cancel-DlpEdmSession, Execute-AzureAdLabelSync, ' +
          'Check-PurviewConfig, Export-PurviewConfig and Validate-RetentionRuleQuery), which ' +
          'DriftWatch never runs. Each assessment discards what it read from Purview if the ' +
          'account holds any Entra directory role other than Teams Reader or the session offers any other command that ' +
          'is not a read. The residual risk of each is published at the permissions endpoint.',
        'Tenant isolation, enforced twice. Every table holding tenant data carries a tenant ' +
          'identifier; every repository function that touches one runs inside an explicit tenant ' +
          'context; and a PostgreSQL row-level-security policy enforces the same boundary again at ' +
          'the database, including for the owning role. The isolation test asserts the invariant ' +
          'over the schema rather than table by table: any table with a tenant identifier column ' +
          'and no isolation policy fails it by name. Tables with no tenant identifier are ' +
          'deployment-level and are reached only through an explicitly named function, so each ' +
          'exemption is visible at every call site and argued in an architecture decision record.',
        'Encryption. Stored tenant credentials are protected by envelope encryption, wrapped by a ' +
          'master key generated on and held only by the host for that environment; the staging ' +
          'and production keys are separate and are never shared, so a compromise of one does not ' +
          'decrypt the other’s credentials. Traffic is served over TLS with certificates issued ' +
          'by Let’s Encrypt and renewed automatically.',
        'Authentication and authorisation. Operator passwords are stored as scrypt hashes and ' +
          'cannot be reversed. Sessions are held in the database and referenced by an httpOnly ' +
          'cookie the console’s own JavaScript cannot read. Authorisation is capability-based and ' +
          'denies by default: an operator can do only what a capability explicitly grants. ' +
          'Sign-in attempts are recorded.',
        'Outbound data control. A single redaction chokepoint is the only path to a third-party ' +
          'model. Evidence is classified when it is constructed rather than filtered by pattern ' +
          'afterwards, because pattern-matching over a payload fails in both directions — it ' +
          'strips policy identifiers the remediation text needs and misses a policy somebody named ' +
          'after a colleague. Classified values become stable pseudonyms; only counts, ' +
          'thresholds, enumerated values, the names of Microsoft’s built-in roles and ' +
          'Microsoft’s published constants pass through. A leak scanner then checks the redacted ' +
          'payload and raises an error on an email address, an external guest identifier, an ' +
          'object identifier that is not a Microsoft constant, or any value that should have been ' +
          'pseudonymised, so the system fails closed on what it recognises; the refusal never ' +
          'records the blocked value. Where the narrative layer is used is set out at ' +
          'anthropic-optional.',
        'Accountability. Operator actions are recorded in an audit log. Every finding carries the ' +
          'evidence that produced it and the semantic version of the rule that decided it, so a ' +
          'verdict can be reproduced and explained rather than asserted.',
        'Support tickets. Every ticket read is bound to the account of the operator asking, and ' +
          'a ticket belonging to another account is answered as not found; tests assert both ' +
          'that one account cannot read another’s ticket and that it can read its own. Attached ' +
          'images are accepted only when their contents identify them as PNG, JPEG or WebP, ' +
          'whatever the upload claims, and never as SVG or any other format; they are served ' +
          'only to the owning account and to DriftWatch’s support staff, with headers that stop ' +
          'a browser treating them as anything but an image. The audit log records that a ticket ' +
          'was opened, answered or closed, never its subject, its messages or its images.',
        'Segregation of environments. Staging and production run as separate databases, separate ' +
          'job-queue indexes, separate ports and separate processes, with separate master keys. ' +
          'Production connected its first tenant on 19 September 2026; its master key has no ' +
          'backup yet, so losing it would lose every credential it wraps.',
        'Development and change control. Development is single-maintainer, with automated review ' +
          'and enforced continuous-integration gates: typechecking, linting, layer-boundary ' +
          'enforcement, the full test suite, and one hundred per cent branch coverage on the ' +
          'rules engine and the redaction layer. This is not peer review and is not described as ' +
          'such anywhere, including here.',
        'What is not in place is at annex-not-claimed, and should be read as part of this annex ' +
          'rather than separately from it.',
      ],
    },
    {
      id: 'annex-subprocessors',
      heading: 'Annex 3 — Sub-processors',
      paragraphs: [
        'The authorised sub-processors at the date of this draft are:',
        'Microsoft is not on this list and will not be. The Tenant assessed is the Customer’s own ' +
          'service under the Customer’s own agreement with Microsoft: it is where the data comes ' +
          'from, not somewhere DriftWatch sends it.',
        'Changes to this list follow the notice and objection process in the sub-processors ' +
          'section above. The same list appears for data subjects in the privacy policy, section ' +
          'subprocessors.',
      ],
      bullets: [
        'IONOS SE — hosting, database and compute — Frankfurt am Main, Germany — always engaged.',
        'Anthropic PBC — generation of narrative text from redacted findings — United States — ' +
          'data: Tenant configuration findings with every customer-specific identifier replaced ' +
          'by a pseudonym, designed to carry no name, email address or object identifier of any ' +
          'person — ' +
          'transfer mechanism: ' +
          '[TRANSFER MECHANISM], not yet in place (see transfers) — engaged in staging, which ' +
          'assesses one real customer Tenant; switched off in production since 21 September 2026, ' +
          'after it was used there on 19 September 2026. It was engaged in staging before this ' +
          'list said so, without the notice described in the sub-processors section.',
        'Let’s Encrypt, operated by the Internet Security Research Group — issuance of TLS ' +
          'certificates — United States — no customer personal data is disclosed to it.',
      ],
    },
    {
      id: 'annex-not-claimed',
      heading: 'Annex 4 — What DriftWatch does not have',
      paragraphs: [
        'A DPA annex is where a supplier usually lists its certifications. This one lists their ' +
          'absence instead, because DriftWatch assesses other organisations against these ' +
          'frameworks and does not hold them, and a control description that overstates itself is ' +
          'worse than one that admits a gap: the gap is a finding, the overstatement is a ' +
          'misrepresentation.',
        'Each of these is a gap that is known, recorded and closeable. None of them is a reason ' +
          'the measures in annex-security-measures are not real; those are mechanisms, and they ' +
          'can be demonstrated on request.',
      ],
      bullets: [
        'No SOC 2 report.',
        'No ISO/IEC 27001 certification.',
        'No penetration test.',
        'No service level agreement or uptime commitment.',
        'No off-site backups. Nightly database dumps are kept for seven days on the same host, ' +
          'which is not disaster recovery and is not described as such.',
        'No automatic retention or deletion schedule. See the deletion section.',
        'No peer review of code changes. Development is single-maintainer with automated review ' +
          'and enforced continuous-integration gates.',
        'No cyber insurance position is stated in this draft, because none has been established.',
      ],
    },
    {
      id: 'placeholders',
      heading: 'Placeholders in this draft',
      paragraphs: [
        'Every square-bracketed placeholder in this document is listed here. This agreement must ' +
          'not be executed while any of them remain unresolved.',
      ],
      bullets: [
        '[COMPANY NUMBER] — Drift Watch’s company registration number.',
        '[REGISTERED ADDRESS] — Drift Watch’s registered office.',
        '[CUSTOMER NAME] — the counterparty to this agreement.',
        '[CUSTOMER REGISTERED ADDRESS] — the counterparty’s registered office.',
        '[EFFECTIVE DATE] — the date this agreement takes effect.',
        '[GOVERNING LAW] — the governing law and the courts having jurisdiction.',
        '[PRIVACY CONTACT EMAIL] — the contact address for matters under this agreement.',
        '[SUPERVISORY AUTHORITY] — DriftWatch’s lead supervisory authority, which follows from ' +
          'where the entity is established.',
        '[DPO OR REPRESENTATIVE] — a data protection officer or Article 27 representative, if one ' +
          'is required or appointed.',
        '[SUB-PROCESSOR OBJECTION PERIOD] — the notice period before a sub-processor change, and ' +
          'the window in which the Customer may object.',
        '[BREACH NOTIFICATION DEADLINE] — the contractual long-stop for notifying the Customer of ' +
          'a personal data breach, in addition to the "without undue delay" obligation.',
        '[TRANSFER MECHANISM] — the mechanism relied on for the transfer to Anthropic PBC, which ' +
          'staging already makes, and the transfer risk assessment that must accompany it. ' +
          'Neither is in place yet.',
        '[RETENTION SCHEDULE] — the retention and deletion schedule that does not exist yet, and ' +
          'whose absence is recorded as a gap in the deletion section and at annex-not-claimed.',
      ],
    },
  ],
};
